import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Segmented } from "../../../components/ui";
import { apiError, previewThanks, sendThanks } from "../../../services/paymentsLedger";
import MailQuotaNotice from "../emails/MailQuotaNotice";

/**
 * Thank one payer or a selection: generic letter signed by Alain Foka (adapted
 * to the payment: donation, subscription, purchase) or a written message.
 * The preview shows the exact email of the first payment of the selection.
 */
const ThanksComposer = ({ ids, alreadyThanked = 0, onClose, onSent }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("generic");
  const [lang, setLang] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [signed, setSigned] = useState(true);
  const [force, setForce] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [sending, setSending] = useState(false);

  const options = { mode, lang: lang || undefined, subject, message, signed, force };
  const ready = mode === "generic" || (subject.trim() && message.trim());

  // Preview refreshed while typing (first payment of the selection)
  useEffect(() => {
    if (!ready) {
      setPreview(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      previewThanks(ids[0], options)
        .then((email) => {
          setPreview(email);
          setPreviewError("");
        })
        .catch((error) => {
          setPreview(null);
          setPreviewError(apiError(error) || t("Preview unavailable"));
        });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, lang, subject, message, signed, ids[0]]);

  const send = async () => {
    setSending(true);
    try {
      const result = await sendThanks(ids, options);
      if (result.sent) toast.success(t("thanksSent", { count: result.sent }));
      if (result.skipped?.length) toast.info(t("thanksSkipped", { count: result.skipped.length }));
      if (result.failed?.length) toast.error(result.failed[0].reason);
      if (!result.sent && !result.skipped?.length && !result.failed?.length) toast.info(t("Nothing to send"));
      onSent(result);
    } catch (error) {
      toast.error(apiError(error) || t("Error updating data"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} centered size="xl" dialogClassName="ui-modal">
      <Modal.Header>
        <Modal.Title>
          {ids.length > 1 ? t("thankSelection", { count: ids.length }) : t("Thank the payer")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ledger-composer">
          <div className="ui-form ledger-composer-form">
            <div className="ui-field ui-field-full">
              <Segmented
                label={t("Message type")}
                value={mode}
                onChange={setMode}
                options={[
                  { value: "generic", label: t("Letter from Alain Foka") },
                  { value: "custom", label: t("Written message") },
                ]}
              />
              <p className="ui-hint">
                {mode === "generic"
                  ? t("A thank-you letter signed by Alain Foka, adapted to each payment (donation, subscription, purchase) and to the payer's language.")
                  : t("Your own subject and message. Leave an empty line between paragraphs.")}
              </p>
            </div>
            {mode === "custom" && (
              <>
                <div className="ui-field ui-field-full">
                  <label htmlFor="thanks-subject">{t("Subject")}</label>
                  <input id="thanks-subject" className="ui-input" value={subject} maxLength={150} onChange={(e) => setSubject(e.target.value)} />
                </div>
                <div className="ui-field ui-field-full">
                  <label htmlFor="thanks-message">{t("Message")}</label>
                  <textarea id="thanks-message" className="ui-input ledger-textarea" rows={9} maxLength={5000} value={message} onChange={(e) => setMessage(e.target.value)} />
                </div>
                <label className="ledger-check">
                  <input type="checkbox" checked={signed} onChange={(e) => setSigned(e.target.checked)} />
                  {t("Sign as Alain Foka")}
                </label>
              </>
            )}
            <div className="ui-field">
              <label htmlFor="thanks-lang">{t("Language")}</label>
              <select id="thanks-lang" className="ui-input" value={lang} onChange={(e) => setLang(e.target.value)}>
                <option value="">{t("Payer's language")}</option>
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>
            {alreadyThanked > 0 && (
              <label className="ledger-check">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
                {t("alreadyThankedResend", { count: alreadyThanked })}
              </label>
            )}
          </div>

          <div className="ledger-preview">
            <span className="ui-label">
              {t("Preview")}
              {preview?.to && ` · ${t("to")} ${preview.to}`}
            </span>
            {preview ? (
              <>
                <p className="ledger-preview-subject">{preview.subject}</p>
                <iframe title={t("Preview")} className="ledger-preview-frame" srcDoc={preview.html} sandbox="" />
              </>
            ) : (
              <p className="ui-hint">{previewError || (ready ? t("Loading…") : t("Write the subject and the message to see the email."))}</p>
            )}
          </div>
        </div>
      </Modal.Body>
      <div className="mail-quota-slot">
        <MailQuotaNotice recipients={ids.length} />
      </div>
      <Modal.Footer>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose}>
          {t("Cancel")}
        </button>
        <button type="button" className="ui-btn" disabled={!ready || sending || (ids.length === 1 && Boolean(previewError))} onClick={send}>
          <i className="ri-mail-send-line" aria-hidden="true"></i>
          {sending ? t("Sending…") : ids.length > 1 ? t("sendToCount", { count: ids.length }) : t("Send")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ThanksComposer;
